import { ApexAxisChartSeries, ApexFormatterOpts, ApexNonAxisChartSeries, ApexOptions } from "apexcharts";
import { format } from "date-fns";
import * as _ from "lodash";
import { BehaviorSubject } from "rxjs";
import { PricePeriods } from "src/app/models/PricePeriod";

export class ConsumptionYearData {
    year: number;
    totalConsumption: number;
    totalPriceByOMIP: number;
    priceAverage: number;
    totalPrice: number;

    monthsWithPricesApplied: number[] = []
    consumptionOfMonthsWithPricesApplied: number = 0

    // MWh protected by locked prices (the contract's covers and the selected offers), and the € amounts split between locked and market-priced consumption.
    // The contract's part is already fixed, so it has no cost here: coveredCost only holds the offers.
    coveredConsumption: number = 0
    coveredCost: number = 0
    uncoveredCostEstimate: number = 0

    // Locked cost split by cover; their costs add up to coveredCost
    lockedParts: LockedCostPart[] = []

    quarters: ConsumptionQuarterData[] = []

    constructor(year: number, consumption: number = 0, priceOMIP: number = 0, priceAvg: number = 0, price: number) {
        this.year = year;
        this.totalConsumption = consumption
        this.totalPriceByOMIP = priceOMIP
        this.priceAverage = priceAvg
        this.totalPrice = price
    }

    // Some cover was scaled down in a month of this year because the covers went past 100% of the consumption
    get hasCappedLockedPart(): boolean {
        return this.lockedParts.some((part) => part.isCapped);
    }

    get coverPercentage(): number {
        return this.totalConsumption > 0 ? (this.coveredConsumption / this.totalConsumption) * 100 : 0;
    }

    // Part of coveredConsumption already fixed by the contract's own covers
    contractCoveredConsumption: number = 0

    get contractCoverPercentage(): number {
        return this.totalConsumption > 0 ? (this.contractCoveredConsumption / this.totalConsumption) * 100 : 0;
    }

    // Part of coveredConsumption protected by the selected offers
    get offerCoveredConsumption(): number {
        return Math.max(0, this.coveredConsumption - this.contractCoveredConsumption);
    }

    // Contract covers plus the selected offers (both already in coveredConsumption), capped at 100
    get totalCoverPercentage(): number {
        return Math.min(100, this.coverPercentage);
    }

    get uncoveredConsumption(): number {
        return Math.max(0, this.totalConsumption - this.coveredConsumption);
    }

    get totalCostEstimate(): number {
        return this.coveredCost + this.uncoveredCostEstimate;
    }

    // Consumption with a cost in the simulation: everything but the contract's part
    get costedConsumption(): number {
        return Math.max(0, this.totalConsumption - this.contractCoveredConsumption);
    }

    // Average €/MWh paid for the part locked by the selected offers
    get lockedAveragePrice(): number {
        return this.offerCoveredConsumption > 0 ? this.coveredCost / this.offerCoveredConsumption : 0;
    }

    // Average €/MWh for the year's costed consumption (locked + market estimate)
    get averagePriceEstimate(): number {
        return this.costedConsumption > 0 ? this.totalCostEstimate / this.costedConsumption : 0;
    }

    // MWh-weighted sums over the unprotected consumption, used to show the indexed price formula with this year's values
    indexedWeightedOmip: number = 0
    indexedWeightedProfileFactor: number = 0
    indexedWeightedLossFactor: number = 0

    get averageIndexedPrice(): number {
        return this.uncoveredConsumption > 0 ? this.uncoveredCostEstimate / this.uncoveredConsumption : 0;
    }

    get averageOmip(): number {
        return this.uncoveredConsumption > 0 ? this.indexedWeightedOmip / this.uncoveredConsumption : 0;
    }

    get averageProfileFactor(): number {
        return this.uncoveredConsumption > 0 ? this.indexedWeightedProfileFactor / this.uncoveredConsumption : 0;
    }

    get averageLossFactor(): number {
        return this.uncoveredConsumption > 0 ? this.indexedWeightedLossFactor / this.uncoveredConsumption : 0;
    }
}

// What one locked price (cover) contributes to a year: the MWh it protects in that year's months and what they cost at its price.
export class LockedCostPart {
    priceId: string;
    label: string;
    coverPercentage: number;
    // Its cover % was scaled down in some month because, with the contract's covers, it went past 100% of the consumption
    isCapped: boolean = false
    consumption: number = 0
    cost: number = 0

    constructor(priceId: string, label: string, coverPercentage: number) {
        this.priceId = priceId;
        this.label = label;
        this.coverPercentage = coverPercentage;
    }

    // €/MWh of this cover over the year's months it applies to
    get averagePrice(): number {
        return this.consumption > 0 ? this.cost / this.consumption : 0;
    }
}

// Estimate of the €/MWh paid by the unprotected (indexed) consumption, following the usual Iberian indexed tariff structure:
//   indexed price = (OMIP × profile factor + system costs) × (1 + grid losses) + supplier fee
// OMIP is a baseload forward, so the profile factor adjusts it to the client's consumption shape across tariff periods.
// Grid losses depend on the voltage level of each meter. System costs and the supplier fee are fixed adders in €/MWh.
export namespace IndexedPriceEstimate {
    // Price of each tariff period relative to the baseload price (weighted by the hours of each period this averages ~1.00)
    export const PROFILE_COEFFICIENTS = {
        peak: 1.12,
        standard: 1.05,
        offPeak: 0.94,
        superOffPeak: 0.88
    }

    // Average grid loss factors by voltage level
    export const LOSS_FACTORS_BY_TENSION_LEVEL: { [tensionLevel: string]: number } = {
        MAT: 0.02,
        AT: 0.035,
        MT: 0.075,
        BTE: 0.15,
        BTN: 0.15
    }
    export const DEFAULT_LOSS_FACTOR = LOSS_FACTORS_BY_TENSION_LEVEL['MT']

    // System management costs (balancing, ancillary services, capacity mechanisms) in €/MWh
    export const SYSTEM_COSTS = 5
    // Supplier fee for an indexed contract in €/MWh
    export const SUPPLIER_FEE = 4

    // Consumption-weighted profile coefficient. Falls back to 1 (baseload) when there's no period split.
    export function getProfileFactor(consumption: { peak: number, standard: number, offPeak: number, superOffPeak: number }): number {
        const total = consumption.peak + consumption.standard + consumption.offPeak + consumption.superOffPeak;

        if (total <= 0) {
            return 1;
        }

        return (
            consumption.peak * PROFILE_COEFFICIENTS.peak
            + consumption.standard * PROFILE_COEFFICIENTS.standard
            + consumption.offPeak * PROFILE_COEFFICIENTS.offPeak
            + consumption.superOffPeak * PROFILE_COEFFICIENTS.superOffPeak
        ) / total;
    }

    export function getLossFactor(tensionLevel?: string): number {
        return LOSS_FACTORS_BY_TENSION_LEVEL[(tensionLevel ?? '').trim().toUpperCase()] ?? DEFAULT_LOSS_FACTOR;
    }

    export function getPrice(omip: number, profileFactor: number, lossFactor: number): number {
        if (omip <= 0) {
            return 0;
        }

        return (omip * profileFactor + SYSTEM_COSTS) * (1 + lossFactor) + SUPPLIER_FEE;
    }
}

// Supplier prices come from the backend as up to four €/kWh values (priceOne..priceFour, stored as peak, standard, offPeak, superOffPeak).
// What each slot means depends on the meter's tariff (PT backend mapping), so consumption is grouped to match:
//   simple     (BTN simples)     → priceOne = flat price for all consumption
//   biHourly   (BTN bi-horário)  → priceOne = fora de vazio (peak + standard), priceTwo = vazio (offPeak + superOffPeak)
//   triHourly  (BTN tri-horário) → priceOne = peak, priceTwo = standard, priceThree = offPeak (+ superOffPeak, BTN has no super vazio)
//   fourPeriods (BTE / MT / AT)  → one price per period (ponta, cheias, vazio, super vazio)
// Bi-horário reads vazio from priceTwo, like the backend's PdfsService (the price import may also write vazio to priceThree).
export namespace ElectricityPriceWeighting {
    export type PeriodValues = { peak: number, standard: number, offPeak: number, superOffPeak: number };

    export type TariffCycle = 'simple' | 'biHourly' | 'triHourly' | 'fourPeriods';

    const SLOTS_BY_TARIFF_CYCLE: { [cycle in TariffCycle]: number } = {
        simple: 1,
        biHourly: 2,
        triHourly: 3,
        fourPeriods: 4
    };

    // Tariff of a meter: BTN uses its counting cycle (simples / bi-horário / tri-horário), the other tension levels price the four periods.
    // Returns undefined when it can't be told, so the caller falls back to the number of filled prices.
    export function getTariffCycle(equipment: { tensionLevelDescription?: string, countingCycleDescription?: string }): TariffCycle | undefined {
        // Lowercase letters only, so "Bi-horário", "BIHOURLY" or "bi_hourly" all start with "bi"
        const normalize = (text?: string) => (text ?? '').toLowerCase().replace(/[^a-z]/g, '');
        const tensionLevel = normalize(equipment.tensionLevelDescription);
        const countingCycle = normalize(equipment.countingCycleDescription);

        if (tensionLevel && tensionLevel != 'btn') {
            return 'fourPeriods';
        }
        if (countingCycle.startsWith('bi')) {
            return 'biHourly';
        }
        if (countingCycle.startsWith('tri')) {
            return 'triHourly';
        }
        if (countingCycle.startsWith('simple')) {
            return 'simple';
        }

        return undefined;
    }

    // Consumption-weighted price of a supplier offer, in €/MWh.
    export function getWeightedPricePerMWh(price: PeriodValues, consumption: PeriodValues, tariffCycle?: TariffCycle): number {
        const prices = [price.peak, price.standard, price.offPeak, price.superOffPeak].map((value) => Number(value) || 0);
        // Without a known tariff, the number of priced slots is the position of the last filled price (slots are filled from priceOne onwards)
        const pricedSlots = tariffCycle
            ? SLOTS_BY_TARIFF_CYCLE[tariffCycle]
            : prices.reduce((count, value, index) => value > 0 ? index + 1 : count, 0);

        if (pricedSlots == 0) {
            return 0;
        }

        const [peak, standard, offPeak, superOffPeak] = [consumption.peak, consumption.standard, consumption.offPeak, consumption.superOffPeak]
            .map((value) => Math.max(0, Number(value) || 0));

        const consumptionBySlot = pricedSlots == 1 ? [peak + standard + offPeak + superOffPeak]
            : pricedSlots == 2 ? [peak + standard, offPeak + superOffPeak]
            : pricedSlots == 3 ? [peak, standard, offPeak + superOffPeak]
            : [peak, standard, offPeak, superOffPeak];

        const totalConsumption = _.sum(consumptionBySlot);

        // Without a consumption split, fall back to the plain average of the tariff's prices
        const pricePerKWh = totalConsumption > 0
            ? _.sum(consumptionBySlot.map((slotConsumption, index) => slotConsumption * prices[index])) / totalConsumption
            : _.mean(prices.slice(0, pricedSlots));

        return pricePerKWh * 1000;
    }
}

export class ConsumptionQuarterData {
    quarter: number;
    totalConsumption: number;
    totalPriceByOMIP: number;
    priceAverage: number;
    
    months: ConsumptionMonthData[] = []

    isHighestConsumptionQuarterOfYear: boolean = false

    constructor(quarter: number, consumption: number = 0, priceOMIP: number = 0, price: number = 0) {
        this.quarter = quarter
        this.totalConsumption = consumption
        this.totalPriceByOMIP = priceOMIP
        this.priceAverage = price
    }
}

export class ConsumptionMonthData {
    month: number;
    totalConsumption: number;
    totalPriceByOMIP: number;
    priceAverage: number;

    monthAsString?: string

    coverPercentage: number = 0
    contractCoverPercentage: number = 0
    coveredConsumption: number = 0
    // Part of coveredConsumption fixed by the contract's own covers, which has no cost in the simulation
    contractCoveredConsumption: number = 0
    coveredCost: number = 0
    uncoveredCostEstimate: number = 0
    marketPrice: number = 0
    indexedPrice: number = 0

    constructor(month: number, consumption: number = 0, priceOMIP: number = 0, price: number = 0) {
        this.month = month
        this.totalConsumption = consumption
        this.totalPriceByOMIP = priceOMIP
        this.priceAverage = price
    }
}

export interface OmipTrend {
    percentage: number;
    direction: 'up' | 'down' | 'stable';
}

export class PricePeriodsSimulation {
    year?: number;
    price?: PricePeriods.ElectricityPrices;
    priceElement?: HTMLElement;
    targetContainer?: HTMLElement; 

    coverPercentage?: number;

    constructor() {}
}


export class PriceContainer {
    price: PricePeriods.ElectricityPrices
    coverPercentage: number
    columnStart: number
    columnEnd: number
    rowStart: number
    rowEnd: number
    startOfPriceYear: number
    endOfPriceYear: number
    // Covers that come from the selected contract's fixed economic conditions can't be edited or removed
    isContractCover: boolean = false

    constructor(
        price: PricePeriods.ElectricityPrices,
        coverPercentage: number,
        columnStart: number,
        columnEnd: number,
        rowStart: number,
        rowEnd: number,
        startOfPriceYear: number,
        endOfPriceYear: number,
    ) {
        this.price = price;
        this.coverPercentage = coverPercentage;
        this.columnStart = columnStart;
        this.columnEnd = columnEnd;
        this.rowStart = rowStart;
        this.rowEnd = rowEnd;
        this.startOfPriceYear = startOfPriceYear;
        this.endOfPriceYear = endOfPriceYear;
    }

    get gridColumn(): string {
        return `${this.columnStart} / ${this.columnEnd}`;
    }

    get gridRow(): string {
        return `${this.rowStart} / ${this.rowEnd}`;
    }

    getClass(year: number) {
        let classes = ''
        if (this.startOfPriceYear == year) {
            classes += ' start-year'
        }
        if (this.endOfPriceYear == year) {
            classes += ' end-year'
        }
        if (this.isContractCover) {
            classes += ' contract-cover'
        }

        return classes;
    }
}

// Blue = protected (locked price), grey = total / indexed (variable), dark = the resulting average. Used by the simulation chart and its tooltip.
export const HEDGING_SERIES_COLORS = {
    TOTAL_CONSUMPTION: '#b5bac8',
    PROTECTED_CONSUMPTION: '#5183C2',
    COVER_PRICE: '#5183C2',
    OMIP_PRICE_PROJECTION: '#8e93a6',
    FINAL_PRICE: '#1e2b45'
}

export namespace CustomChartTooltips {
    // Builds the cover simulation tooltip as short sentences (consumption, protected part, indexed part, average price) from the month's simulation data.
    export function getCoverSimulationChart(
        month: { date: Date, data: ConsumptionMonthData } | undefined,
        translationStream: { [key: string]: string },
        userCountryLocale: Locale
    ): string {
        if (!month) {
            return '';
        }

        const formatNumber = (value: number, decimals: number = 2) => value.toLocaleString(userCountryLocale.code ?? 'pt-PT', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
        // Consumptions show up to 3 decimals
        const formatConsumption = (value: number) => value.toLocaleString(userCountryLocale.code ?? 'pt-PT', {
            maximumFractionDigits: 3
        });
        const translate =(key: string, params: { [key: string]: string } = {}) =>
            (translationStream[key] ?? key).replace(/{{\s*(\w+)\s*}}/g, (_match, param) => params[param] ?? '');

        const { totalConsumption, coverPercentage, coveredConsumption, contractCoveredConsumption, coveredCost, uncoveredCostEstimate, indexedPrice } = month.data;
        // The contract's part has no cost here, so it's left out of both averages
        const offerCoveredConsumption = coveredConsumption - contractCoveredConsumption;
        const costedConsumption = totalConsumption - contractCoveredConsumption;
        const lockedPrice = offerCoveredConsumption > 0 ? coveredCost / offerCoveredConsumption : 0;
        const averagePrice = costedConsumption > 0 ? (coveredCost + uncoveredCostEstimate) / costedConsumption : 0;

        const line = (color: string | null, text: string) => `
            <div style="display: flex; align-items: center; gap: 8px; padding: 2px 0;">
                <span style="flex: none; width: 10px; height: 10px; border-radius: 2px; background-color: ${color ?? 'transparent'};"></span>
                <span>${text}</span>
            </div>`;

        const lines = [
            line(HEDGING_SERIES_COLORS.TOTAL_CONSUMPTION, translate('TOOLTIP_ESTIMATED_CONSUMPTION', { value: formatConsumption(totalConsumption) })),
            coverPercentage > 0
                ? line(HEDGING_SERIES_COLORS.PROTECTED_CONSUMPTION, translate('TOOLTIP_PROTECTED_AT', { percentage: formatNumber(coverPercentage, 0), price: formatNumber(lockedPrice) }))
                : line(HEDGING_SERIES_COLORS.TOTAL_CONSUMPTION, translate('TOOLTIP_NOTHING_PROTECTED')),
            coverPercentage < 100 && indexedPrice > 0
                ? line(HEDGING_SERIES_COLORS.OMIP_PRICE_PROJECTION, translate('TOOLTIP_REST_AT_INDEXED', { price: formatNumber(indexedPrice) }))
                : ''
        ];

        const average = averagePrice > 0
            ? `<div style="border-top: 1px solid #eff2f5; margin-top: 6px; padding-top: 6px;">
                   ${line(HEDGING_SERIES_COLORS.FINAL_PRICE, `<strong>${translate('TOOLTIP_AVERAGE_PRICE', { price: formatNumber(averagePrice) })}</strong>`)}
               </div>`
            : '';

        return `
            <div style="font-family: inherit; font-size: 12px; padding: 8px 12px; min-width: 220px;">
                <div style="font-weight: 600; margin-bottom: 4px;">
                    ${_.upperFirst(format(month.date, 'MMMM yyyy', { locale: userCountryLocale }))}
                </div>
                ${lines.join('')}
                ${average}
            </div>
        `;
    }
}